/**
 * Verzendadressen ("Verzenden vanaf") voor de mass mail.
 *
 * ⚠️ Deze lijst moet gelijk blijven met `allowedSenders` in de backend
 * (`yallyhub-be/src/shared/email/email.controller.ts`, endpoints
 * `/email/send-mass-email` en `/email/retry-failed-emails`). Een adres dat hier
 * wél staat maar daar niet, geeft bij verzenden een 400
 * "Invalid sender email" — de mail vertrekt dan niet.
 */

/** Standaard verzendadres wanneer de aanroeper niets meegeeft. */
export const DEFAULT_SENDER_EMAIL = 'ella@yally.be';

/** Adres waarvoor de composer automatisch de "niet antwoorden"-melding toevoegt. */
export const NOREPLY_SENDER_EMAIL = 'noreply@yally.be';

export interface SenderOption {
  /** Het effectieve From-adres. */
  email: string;
  /** Korte Nederlandse toelichting, zodat de keuze duidelijk is in de dropdown. */
  description: string;
}

/** Standaardadres eerst: dat is wat de gebruiker in de meeste gevallen wil. */
export const MASS_MAIL_SENDER_OPTIONS: SenderOption[] = [
  { email: DEFAULT_SENDER_EMAIL, description: 'Algemeen (standaard)' },
  { email: 'helpdesk@yally.be', description: 'Helpdesk — antwoorden komen in de helpdeskmailbox' },
  { email: 'rent@yally.be', description: 'Verhuur' },
  { email: 'incasso@yally.be', description: 'Incasso' },
  { email: NOREPLY_SENDER_EMAIL, description: 'Antwoorden niet mogelijk' },
];

/**
 * Opties voor de dropdown, inclusief het huidige adres.
 *
 * Een aanroeper kan via `initialState.senderEmail` een adres zetten dat niet in
 * de vaste lijst staat. Zonder die extra optie zou de dropdown leeg lijken
 * terwijl er wél een afzender is — daarom wordt het meegegeven adres vooraan
 * toegevoegd in plaats van genegeerd.
 */
export const resolveSenderOptions = (currentSenderEmail?: string): SenderOption[] => {
  const current = currentSenderEmail?.trim();
  if (!current) return MASS_MAIL_SENDER_OPTIONS;

  const isKnown = MASS_MAIL_SENDER_OPTIONS.some(
    (option) => option.email.toLowerCase() === current.toLowerCase()
  );
  if (isKnown) return MASS_MAIL_SENDER_OPTIONS;

  return [{ email: current, description: 'Meegegeven door module' }, ...MASS_MAIL_SENDER_OPTIONS];
};

export interface SenderFieldState {
  /** true = dropdown, false = vast adres dat de gebruiker niet kan wijzigen. */
  editable: boolean;
  /** Het adres dat verstuurd wordt (en dus getoond moet worden). */
  email: string;
  /** Alleen gevuld wanneer `editable` — de keuzes voor de dropdown. */
  options: SenderOption[];
}

/**
 * Bepaalt of de composer een dropdown of een vast adres toont.
 *
 * Het afzenderadres is standaard **vast**: een mail vanuit een module hoort van
 * die module te komen (helpdeskticket → helpdesk@yally.be, renoticket →
 * leo@yally.be, incassodossier → incasso@yally.be, ...). Alleen de losse mass
 * mail via de plusknop in de navigatiebalk zet `allowSenderSelection: true` en
 * laat de gebruiker zelf kiezen.
 *
 * `lockedSenderEmail` blijft ondersteund voor bestaande aanroepers en wint altijd:
 * het adres is dan niet enkel vast, het overschrijft ook `senderEmail`.
 */
export const resolveSenderField = (input: {
  senderEmail?: string;
  lockedSenderEmail?: string;
  allowSenderSelection?: boolean;
}): SenderFieldState => {
  const locked = input.lockedSenderEmail?.trim();
  if (locked) {
    return { editable: false, email: locked, options: [] };
  }

  const email = input.senderEmail?.trim() || DEFAULT_SENDER_EMAIL;
  if (!input.allowSenderSelection) {
    return { editable: false, email, options: [] };
  }

  return { editable: true, email, options: resolveSenderOptions(email) };
};
